import React, { useRef, useState } from "react";
import EventOutlined from "@mui/icons-material/EventOutlined";
import Close from "@mui/icons-material/Close";
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  InputAdornment,
  Popover,
  TextField,
  SxProps,
} from "@mui/material";
import ButtonGroupCustom from "components/ButtonGroupCustom/ButtonGroupCustom";
import { DateRangePicker } from "components/DatePickers/DateRangePicker";
import { DatePickerDrawer } from "./DatePickerDrawer";
import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";
import { changeFlexibleSearch } from "redux/searchForm/searchForm.slice";
import { IFlight, IFlightDates, searchFormSelector } from "redux/searchForm";

import { useTranslations } from "hooks/useTranslations";
import {
  MediaQuery,
  dateStringToDateObj,
  deepCloneObject,
  formatDateShort,
  useConfig,
} from "@thekilroygroup/shared-ui";

interface IProps {
  index: number;
  flights: IFlight[];
  onDateChange: (update: any, index: number) => void;
  onBlur?: (flights: IFlight[]) => void;
}

export default function TravelDates(props: IProps) {
  const {
    index,
    flights,
    onDateChange: onDateChange,
    onBlur = () => {},
  } = props;

  const { tripType, flexibleSearch } = useSelector(searchFormSelector);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);
  const { translations } = useTranslations();
  const translationsSearch = translations.search;
  const { locale } = useConfig();
  const dispatch = UseAppDispatch();
  const dates: IFlightDates = {
    departureDate: dateStringToDateObj(flights[index].departureDate),
    returnDate:
      tripType === "RETURN"
        ? dateStringToDateObj(flights[1].departureDate)
        : null,
  };

  const handleClose = () => {
    setOpen(false);
    validate();
  };

  const validate = () => {
    let nextFlights: IFlight[] = deepCloneObject(flights);

    if (tripType !== "RETURN") {
      nextFlights[index].validation.departureDate = {
        dirty: true,
        valid: nextFlights[index].departureDate !== null,
      };

      onBlur(nextFlights);
    } else {
      nextFlights[0].validation.departureDate = {
        dirty: true,
        valid: nextFlights[0].departureDate !== null,
      };
      nextFlights[1].validation.departureDate = {
        dirty: true,
        valid: nextFlights[1].departureDate !== null,
      };

      onBlur(nextFlights);
    }
  };

  const formatInputText = () => {
    const { departureDate, returnDate } = { ...dates };
    let returnString = "";
    if (tripType === "RETURN" && departureDate !== null) {
      returnString += `${formatDateShort(departureDate, locale)} -`;
    }
    if (tripType === "RETURN" && returnDate !== null) {
      returnString += ` ${formatDateShort(returnDate, locale)}`;
    }
    if (tripType !== "RETURN" && departureDate !== null) {
      returnString += formatDateShort(departureDate, locale);
    }
    return returnString;
  };

  const getSelectedFlexibility = () => {
    const val = translationsSearch.date.flexibleSearch.values.find(
      (elm) => Number(elm.id) === flexibleSearch,
    );
    if (val !== undefined && flexibleSearch !== 0) {
      return [val];
    }
    return null;
  };

  const getValidState = () => {
    if (tripType !== "RETURN") {
      return (
        flights[index].validation.departureDate.dirty &&
        !flights[index].validation.departureDate.valid
      );
    } else {
      return (
        (flights[0].validation.departureDate.dirty &&
          !flights[0].validation.departureDate.valid) ||
        (flights[1].validation.departureDate.dirty &&
          !flights[1].validation.departureDate.valid)
      );
    }
  };

  const getErrorText = () => {
    if (getValidState()) {
      if (tripType !== "RETURN") {
        return translationsSearch.date.departure.errorText;
      } else if (
        !flights[0].validation.departureDate.valid &&
        !flights[1].validation.departureDate.valid
      ) {
        return translationsSearch.date.departureReturn.errorText;
      } else if (!flights[0].validation.departureDate.valid) {
        return translationsSearch.date.departure.errorText;
      } else {
        return translationsSearch.date.return.errorText;
      }
    }
    return "";
  };

  const getFirstAvailableDate = () => {
    const previousDateMulti =
      index > 0 ? dateStringToDateObj(flights[index - 1].departureDate) : null;
    if (tripType === "MULTI_DEST" && index > 0 && previousDateMulti !== null) {
      return new Date(previousDateMulti);
    }

    let date = new Date();
    // Set minimum date as three days from today
    return new Date(date.setDate(date.getDate() + 3));
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.code === "Space") {
      setOpen(true);
    }
  };

  /**
   * Change the flexibility eg. +/- 1 or 3 days
   */
  const { input, popOver, drawer, appBar } = sxStyles;

  return (
    <>
      <Box>
        <TextField
          ref={inputRef}
          label={translationsSearch.date.label}
          variant="outlined"
          color="secondary"
          value={formatInputText()}
          sx={input}
          onKeyDown={handleKeyDown}
          InputProps={{
            readOnly: true,
            startAdornment: (
              <InputAdornment position="start">
                <EventOutlined />
              </InputAdornment>
            ),
          }}
          onClick={() => setOpen(true)}
          error={getValidState()}
          helperText={getErrorText()}
        />
        <MediaQuery min="md">
          {open && (
            <Popover
              open={open}
              anchorEl={inputRef?.current || null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              sx={{ ...popOver, p: 50 }}
              onClose={handleClose}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <DateRangePicker
                isRange={tripType === "RETURN"}
                handleDateChange={(dates) => onDateChange(dates, index)}
                selected={dates}
                firstAvailableDate={getFirstAvailableDate()}
                fromMonth={getFirstAvailableDate()}
                setOpen={setOpen}
              />
              <Box sx={{ mb: 2 }}>
                <ButtonGroupCustom
                  selected={getSelectedFlexibility()}
                  options={translationsSearch.date.flexibleSearch.values}
                  onChange={(option) =>
                    dispatch(changeFlexibleSearch(option.id))
                  }
                  className="button-group-custom--flexibility"
                  align="top-right"
                />
              </Box>
            </Popover>
          )}
        </MediaQuery>

        <MediaQuery max="md">
          {open && (
            <Drawer
              anchor="bottom"
              open={open}
              onClose={handleClose}
              sx={drawer}
            >
              <AppBar color="inherit" sx={appBar}>
                <ButtonGroupCustom
                  selected={getSelectedFlexibility()}
                  options={translationsSearch.date.flexibleSearch.values}
                  onChange={(option) =>
                    dispatch(changeFlexibleSearch(option.id))
                  }
                  align="top-left"
                />
              </AppBar>
              <IconButton
                className="closeButton"
                onClick={handleClose}
                color="primary"
              >
                <Close fontSize="medium" color="primary" />
              </IconButton>

              <DateRangePicker
                isRange={tripType === "RETURN"}
                handleDateChange={(dates) => onDateChange(dates, index)}
                selected={dates}
                firstAvailableDate={getFirstAvailableDate()}
                fromMonth={new Date()}
                setOpen={setOpen}
              />
              <DatePickerDrawer
                isReturn={tripType === "RETURN"}
                value={dates}
                closeDrawer={() => setOpen(false)}
              />
            </Drawer>
          )}
        </MediaQuery>
      </Box>
    </>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  input: {
    my: 1,
    mx: 0,
    width: "100%",
    "& label": { textTransform: "capitalize" },
  },
  popOver: {
    "& .MuiPopover-paper": {
      pt: 2,
    },
    "& .button-group-custom--flexibility": {
      py: 0,
      px: 2,
      borderTop: { md: `1px solid` },
      borderColor: { md: "grey.200" },
      paddingTop: { md: 2 },
    },
  },
  drawer: {
    "& .MuiDrawer-paper": {
      minHeight: "100vh",
      borderRadius: 0,
    },
    "& .closeButton": {
      right: 8,
      top: 16,
    },
  },
  appBar: {
    p: 2,
    pb: 0,
  },
};
